import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { dehydrate, QueryCache, QueryClient, type Query, type QueryKey } from '@tanstack/react-query';
import type { PersistedClient } from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';
import { ApiFailure, isNetworkFailure } from './client';

export const PERSIST_MAX_AGE = 90 * 86_400_000;
/**
 * In-memory garbage-collection time. MUST stay below 2^31-1 ms (~24.8 days): TanStack schedules gc
 * with setTimeout, and browsers fire longer timeouts IMMEDIATELY — which would drop every query
 * the moment it is restored from IndexedDB. Persistence (90 days) is governed by PERSIST_MAX_AGE.
 */
export const GC_TIME = 20 * 86_400_000;
export const SCHEMA_VER = 1;
export const PERSIST_KEY = 'kgr-rq';
export const PERSIST_BUSTER = `kgr-${SCHEMA_VER}`;
/** localStorage: the user whose data this device holds (single-user model, see offline/wipe.ts). */
export const LAST_USER_KEY = 'kgr.lastUserId';

/**
 * Query hash → when the server last answered 403/404 for it. TanStack keeps the old data next to that
 * error and REPLACES the error with the next failure (a refetch that fails on the network or with a
 * 5xx), so the error alone forgets that the data is gone: this keeps it (see isQueryGone).
 */
const goneAt = new Map<string, number>();

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isGoneError(error)) goneAt.set(query.queryHash, Date.now());
    },
    onSuccess: (_data, query) => {
      goneAt.delete(query.queryHash);
    },
  }),
  defaultOptions: {
    queries: {
      gcTime: GC_TIME,
      staleTime: 30_000,
      networkMode: 'offlineFirst',
      retry: (count, err) => {
        if (err instanceof ApiFailure && err.status < 500) return false;
        return count < 2;
      },
      refetchOnWindowFocus: true,
    },
    mutations: { networkMode: 'offlineFirst', retry: false },
  },
});

// A removed query takes its mark along (a new one under the same key starts without data).
queryClient.getQueryCache().subscribe((e) => {
  if (e.type === 'removed') goneAt.delete(e.query.queryHash);
});

/** 403/404: the server says the thing is gone or no longer visible to this user. */
function isGoneError(e: unknown): boolean {
  return e instanceof ApiFailure && (e.status === 403 || e.status === 404);
}

/**
 * The server said this query's thing is gone (403/404: deleted, audience narrowed, member
 * deactivated) and no data arrived since: the data TanStack still holds must not be shown or
 * persisted — also after a later refetch failed for another reason (offline, 5xx) and replaced the
 * 404 as the query's error. Fresh data (a successful fetch or setQueryData) ends it.
 */
export function isQueryGone(q: Pick<Query, 'queryHash' | 'state'>): boolean {
  if (q.state.status === 'error' && isGoneError(q.state.error)) return true;
  const at = q.queryHash === undefined ? undefined : goneAt.get(q.queryHash);
  return at !== undefined && q.state.dataUpdatedAt <= at;
}

/** isQueryGone() for the query under `queryKey` in `qc` (false when there is none). */
export function isQueryKeyGone(qc: QueryClient, queryKey: QueryKey): boolean {
  const query = qc.getQueryCache().find({ queryKey, exact: true });
  return !!query && isQueryGone(query);
}

/** Only queries explicitly marked with meta.persist are written to IndexedDB. */
export function shouldPersistQuery(q: Query): boolean {
  // `null` (e.g. "signed out" for ['me']) is never persisted: restoring it would hide a valid session.
  // A failed offline refetch keeps the data (status 'error') and it must stay persisted, or the next
  // offline start would lose it. But when the server answered 403/404 (deleted, audience narrowed,
  // member deactivated) the data TanStack keeps next to the error is exactly what may no longer be
  // shown: it is not written back, also not after a later failure replaced that error (isQueryGone).
  if (q.meta?.persist !== true || q.state.data === null || q.state.data === undefined) return false;
  return !isQueryGone(q);
}

/* ─── Persisted payload, stamped with its owner ─────────────────────────────
 * Every payload carries `owner`: the id of the signed-in user whose queries it holds (its own ['me']
 * query; null when signed out). A payload is restored only when that owner is the device's last
 * signed-in user (LAST_USER_KEY), so a second tab that still holds a previous user's queries (and
 * writes them back after a logout or user switch elsewhere) can never hand them to the next user.
 * Payloads written before the stamp existed are judged by their own ['me'] query the same way. */

type StampedClient = PersistedClient & { owner?: string | null };

/** Id of the user whose data a dehydrated client holds (its ['me'] query), or null. */
function ownerOf(client: PersistedClient): string | null {
  const me = client.clientState?.queries?.find((q) => q.queryKey[0] === 'me')?.state.data as { id?: unknown } | null | undefined;
  return typeof me?.id === 'string' ? me.id : null;
}

/** The persisted form: the dehydrated client stamped with its owner. */
export function serializePersistedClient(client: PersistedClient): string {
  const stamped: StampedClient = { ...client, owner: ownerOf(client) };
  return JSON.stringify(stamped);
}

/** The device's last signed-in user; undefined when localStorage cannot be read (nothing to compare). */
function lastUserId(): string | null | undefined {
  try {
    return localStorage.getItem(LAST_USER_KEY);
  } catch {
    return undefined;
  }
}

/** Parses a stored payload; undefined when it is empty or belongs to another user than the device's last one. */
export function parsePersistedClient(raw: unknown): PersistedClient | undefined {
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  const client = JSON.parse(raw) as StampedClient;
  const owner = client.owner !== undefined ? client.owner : ownerOf(client);
  const last = lastUserId();
  if (last !== undefined && owner !== last) return undefined;
  return client;
}

/** The persisted query cache this device would restore (see parsePersistedClient), if any. */
export async function readPersistedClient(): Promise<PersistedClient | undefined> {
  return parsePersistedClient(await get<string>(PERSIST_KEY));
}

/**
 * Set when another tab wiped the local data (logout / user switch): this tab's queries belong to the
 * previous session and must never be written again before it reloads.
 */
let persistSuspended = false;
export function suspendPersistence(): void {
  persistSuspended = true;
}

export const persister = createAsyncStoragePersister({
  storage: {
    getItem: (k: string) => get<string>(k).then((v) => v ?? null),
    setItem: (k: string, v: string) => (persistSuspended ? Promise.resolve() : set(k, v)),
    removeItem: (k: string) => del(k),
  },
  key: PERSIST_KEY,
  serialize: serializePersistedClient,
  // A payload of another user restores as nothing (the persister then simply restores no client).
  deserialize: (raw: string) => parsePersistedClient(raw) as PersistedClient,
  // Short: iOS can kill a backgrounded web app at any moment; a long throttle loses recent data.
  throttleTime: 300,
});

/**
 * Writes the cache NOW (unthrottled). Called when the app is hidden / unloaded so the latest state
 * (e.g. a song just saved for offline) survives iOS killing the app.
 */
export function flushPersistedCache(): Promise<void> {
  if (persistSuspended) return Promise.resolve();
  try {
    const client: PersistedClient = { buster: PERSIST_BUSTER, timestamp: Date.now(), clientState: dehydrate(queryClient, { shouldDehydrateQuery: shouldPersistQuery }) };
    return set(PERSIST_KEY, serializePersistedClient(client)).catch(() => undefined);
  } catch {
    return Promise.resolve();
  }
}

export { isNetworkFailure };

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { persist?: boolean };
  }
}
